import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";

import { auth, googleProvider } from "config/index.js";
import { QUERY_KEYS } from "hooks/useApi.js";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Handle authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        // Clear all cached data when user signs out
        queryClient.clear();
      }

      setLoading(false);
    });

    // Listen for payment success messages from payment tab
    const handleMessage = (event) => {
      // Only listen to messages from our own origin
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "PAYMENT_SUCCESS") {
        // Refresh user data to show updated credits
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.USER_PROFILE],
        });

        // Force refetch immediately
        queryClient.refetchQueries({
          queryKey: [QUERY_KEYS.USER_PROFILE],
        });

        // Also refresh billing packages in case they changed
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.BILLING_PACKAGES],
        });
      }
    };

    // Handle page visibility changes to refresh credits when user returns
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // User came back to the tab, refresh profile data
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.USER_PROFILE],
        });
      }
    };

    // Expose refresh method to window for payment success page
    window.refreshUserData = () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_PROFILE],
      });
      queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.USER_PROFILE],
      });
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribe();
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Clean up the window method
      delete window.refreshUserData;
    };
  }, [queryClient, user]);

  const signInWithGoogle = async () => {
    try {
      const signIn = import.meta.env.DEV ? signInWithRedirect : signInWithPopup;
      const result = await signIn(auth, googleProvider);
      // onAuthStateChanged will handle user creation
      return result.user;
    } catch (error) {
      // Make sure we're signed out if anything fails
      await firebaseSignOut(auth);
      setUser(null);
      console.error("Error signing in with Google:", error);
      alert("Failed to sign in with Google. Please try again.");
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // Clear all cached data
      queryClient.clear();
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_PROFILE],
      });
    } catch (error) {
      console.error("Error refreshing user:", error);
      throw error;
    }
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signOut,
        refreshUser,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
